import type { Column } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface DataTableColumnHeaderProps<TData, TValue>
	extends React.HTMLAttributes<HTMLDivElement> {
	column: Column<TData, TValue>;
	title: string;
}

function DataTableColumnHeader<TData, TValue>({
	column,
	title,
	className,
}: DataTableColumnHeaderProps<TData, TValue>) {
	if (!column.getCanSort()) {
		return <div className={cn(className)}>{title}</div>;
	}

	const sorted = column.getIsSorted();
	const sortIndex = column.getSortIndex();
	const isMultiSorted = sortIndex > -1 && column.getSortIndex() !== undefined;

	return (
		<div className={cn("flex items-center space-x-2", className)}>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant="ghost"
						size="sm"
						className="-ml-3 h-8 data-[state=open]:bg-accent"
					>
						<span>{title}</span>
						{sorted === "desc" ? (
							<ArrowDown className="ml-1 size-3.5" />
						) : sorted === "asc" ? (
							<ArrowUp className="ml-1 size-3.5" />
						) : (
							<ArrowUpDown className="ml-1 size-3.5" />
						)}
						{isMultiSorted && sortIndex > 0 && (
							<span className="ml-0.5 text-xs text-muted-foreground">
								{sortIndex + 1}
							</span>
						)}
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start">
					<DropdownMenuItem onClick={() => column.toggleSorting(false, true)}>
						<ArrowUp className="text-muted-foreground/70" />
						Croissant
					</DropdownMenuItem>
					<DropdownMenuItem onClick={() => column.toggleSorting(true, true)}>
						<ArrowDown className="text-muted-foreground/70" />
						Décroissant
					</DropdownMenuItem>
					{sorted && (
						<DropdownMenuItem onClick={() => column.clearSorting()}>
							<ArrowUpDown className="text-muted-foreground/70" />
							Réinitialiser
						</DropdownMenuItem>
					)}
					{column.getCanHide() && (
						<>
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
								<EyeOff className="text-muted-foreground/70" />
								Masquer
							</DropdownMenuItem>
						</>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}

export { DataTableColumnHeader };
